/** @file Page fragment shown when the tenant uses SAML SSO — auto-redirects the user. */
import moment from 'moment';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as icons from '@codegrade/icons';
import { Icon } from '@/components/Icon';
import * as models from '@/models';
import * as utils from '@/utils';
import { clsButtonLikeLink } from './cls';
const COUNTDOWN_SECONDS = 5;
/** The UI for starting the SAML login. */
export const SAMLLoginStart = tsx.component({
    name: 'saml-login-start',
    props: {
        tenant: p.ofType().required,
        nextLocation: p.ofType().required,
    },
    /** @returns The initial component state. */
    data() {
        return { startedAt: moment.utc() };
    },
    computed: {
        /** @returns The URL to redirect to. */
        redirectUrl() {
            const loginUrl = models.SSOProvider.loginUrlFromTenant(this.tenant);
            return utils.buildUrl(loginUrl, {
                query: {
                    next: this.nextLocation
                        .map(loc => this.$router.resolve(loc))
                        .map(resolved => resolved.route.fullPath)
                        .extract(),
                },
            });
        },
        /** @returns Seconds remaining until the auto-redirect fires. */
        secondsRemaining() {
            const now = this.$getRootInstance().$getNow(this.startedAt, {
                secondsPrecisionNeeded: COUNTDOWN_SECONDS,
            });
            return Math.max(0, COUNTDOWN_SECONDS - now.diff(this.startedAt, 'seconds'));
        },
    },
    watch: {
        /** Redirects when the countdown reaches zero. */
        secondsRemaining(val) {
            if (val <= 0) {
                window.location.href = this.redirectUrl;
            }
        },
    },
    /** @returns The rendered SAML login start JSX. */
    render() {
        return (<div class="flex flex-col gap-4">
                <p class="body-medium text-pretty">
                    Your institution uses Single Sign-On (SSO) for authentication. You will be
                    redirected to <span>{this.tenant.name}</span>'s login page automatically in{' '}
                    <span>
                        {this.secondsRemaining} second{this.secondsRemaining !== 1 ? 's' : ''}
                    </span>
                    .
                </p>
                <a class={clsButtonLikeLink} href={this.redirectUrl}>
                    Go to {this.tenant.name}'s login page now
                    <Icon icon={icons.ArrowRight}/>
                </a>
            </div>);
    },
});
